import { ActivityIndicator, FlatList, Platform, useWindowDimensions } from "react-native";
import { format, formatISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { AlarmException } from "@src/services/api/types";
import { View } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { memo, useMemo, useCallback, useState, useRef, useImperativeHandle, forwardRef, useEffect } from "react";
import { buildWeeks, getExceptionColors } from "@src/features/schedules/utils/schedule-utils";
import { sizes } from "@src/constants/sizes";
import { DayColumn } from "./DayColumn";
import { Legend } from "./Legend";
import { WeekNavigationBar } from "./WeekNavigationBar";

export interface ScheduleHistoryEntry {
  schedule: AlarmSchedule;
  activeFrom: string;
  activeUntil: string | null;
}

interface WeeklyScheduleProps {
  schedule?: AlarmSchedule;
  exceptions?: AlarmException[];
  timezone?: string;
  scheduleHistory?: ScheduleHistoryEntry[];
  isHistoryLoading?: boolean;
}

export interface WeeklyScheduleRef {
  scrollToPreviousWeek: () => void;
  scrollToNextWeek: () => void;
}

/**
 * Resolves which schedule was active on a given date.
 * For past days with schedule history, finds the entry whose activeFrom/activeUntil
 * brackets the date. For today and future, returns the current schedule.
 */
function resolveScheduleForDate(
  date: Date,
  currentSchedule: AlarmSchedule | undefined,
  history: ScheduleHistoryEntry[] | undefined,
  timezone: string,
  isHistoryLoading: boolean,
): AlarmSchedule | undefined {
  const todayZoned = toZonedTime(new Date(), timezone);
  const todayDateOnly = new Date(todayZoned.getFullYear(), todayZoned.getMonth(), todayZoned.getDate());
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateOnly >= todayDateOnly) return currentSchedule;

  // While history is loading, don't show anything for past days
  if (isHistoryLoading || !history) return undefined;

  if (history.length === 0) return undefined;

  // Use end-of-day for the date being checked so that a schedule applied
  // partway through a day still counts as active for that entire day.
  // Iterate oldest-first but keep the last match so that on changeover
  // days the most recently applied schedule takes priority.
  const endOfDay = new Date(dateOnly.getTime() + 24 * 60 * 60 * 1000 - 1);

  let matched: AlarmSchedule | undefined;
  for (const entry of history) {
    const activeFrom = new Date(entry.activeFrom);
    const activeUntil = entry.activeUntil ? new Date(entry.activeUntil) : null;

    if (endOfDay >= activeFrom && (activeUntil === null || dateOnly < activeUntil)) {
      matched = entry.schedule;
    }
  }

  return matched;
}

const WeeklyScheduleComponent = forwardRef<WeeklyScheduleRef, WeeklyScheduleProps>(function WeeklySchedule(
  {
    schedule,
    exceptions = [],
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    scheduleHistory,
    isHistoryLoading = false,
  },
  ref,
) {
  const today = useMemo(() => toZonedTime(new Date(), timezone), [timezone]);
  const { theme } = useUnistyles();
  const { width: windowWidth } = useWindowDimensions();
  const flatListRef = useRef<FlatList>(null);

  // Always include past weeks for backward navigation (history loads async)
  const weeksBefore = 4;
  const todayPeriodIndex = useMemo(() => {
    if (Platform.OS !== "web") return weeksBefore;
    return Math.floor(weeksBefore / 2);
  }, []);

  const [currentWeekIndex, setCurrentWeekIndex] = useState(todayPeriodIndex);
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(todayPeriodIndex);
  const [containerWidth, setContainerWidth] = useState(0);

  // Use measured container width so web side panels don't break week pagination.
  const width = useMemo(() => {
    if (containerWidth > 0) {
      return containerWidth;
    }
    if (Platform.OS === "web") {
      const maxContentWidth = Math.min(windowWidth, sizes.maxWidthWeb);
      return maxContentWidth;
    }

    return windowWidth;
  }, [containerWidth, windowWidth]);

  // Build periods: fortnights (14 days) on web, weeks (7 days) on mobile
  const periods = useMemo(() => {
    const allWeeks = buildWeeks(today, weeksBefore, 8);
    if (Platform.OS !== "web") return allWeeks;

    // Group consecutive weeks into fortnights
    return allWeeks.reduce<Date[][]>((fortnights, week, i, arr) => {
      if (i % 2 === 0) {
        fortnights.push(arr[i + 1] ? [...week, ...arr[i + 1]] : week);
      }
      return fortnights;
    }, []);
  }, [today]);

  // Calculate the week range label for the currently displayed week (e.g. "Feb 9 - Feb 15, 2026")
  const currentWeekLabel = useMemo(() => {
    const currentWeek = periods[currentPeriodIndex];
    if (!currentWeek || currentWeek.length === 0) return "";

    const firstDay = currentWeek[0];
    const lastDay = currentWeek[currentWeek.length - 1];

    const year = format(lastDay, "yyyy");

    // Week spans two months - show "Mon D - Mon D, YYYY"
    return `${format(firstDay, "MMM d")} - ${format(lastDay, "MMM d")}, ${year}`;
  }, [periods, currentPeriodIndex]);

  const scrollToPeriod = useCallback(
    (delta: number) => {
      const newIndex = currentPeriodIndex + delta;
      if (newIndex >= 0 && newIndex < periods.length) {
        flatListRef.current?.scrollToIndex({ index: newIndex, animated: true });
        setCurrentPeriodIndex(newIndex);
      }
    },
    [currentPeriodIndex, periods.length],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToPreviousWeek: () => scrollToPeriod(-1),
      scrollToNextWeek: () => scrollToPeriod(1),
    }),
    [scrollToPeriod],
  );

  const canNavigate = { prev: currentPeriodIndex > 0, next: currentPeriodIndex < periods.length - 1 };

  const isLoading = isHistoryLoading || (!schedule && !scheduleHistory);

  useEffect(() => {
    if (width > 0 && !isLoading) {
      requestAnimationFrame(() => {
        flatListRef.current?.scrollToIndex({ index: todayPeriodIndex, animated: false });
        setCurrentPeriodIndex(todayPeriodIndex);
        setCurrentWeekIndex(todayPeriodIndex);
      });
    }
  }, [width, todayPeriodIndex, isLoading]);

  const exceptionColors = useMemo(() => {
    const colorMap: Record<string, string> = {};
    const exceptionColorPalette = getExceptionColors();
    const orangeColor = theme.colors.warning[500] as string; // Orange color for override
    const textColor = theme.colors.text as string; // Text color for silence

    exceptions.forEach((ex, index) => {
      colorMap[ex.id] = exceptionColorPalette[index % exceptionColorPalette.length];
    });

    // Also check schedule rules for silence and override
    if (schedule) {
      schedule.rules.forEach((rule) => {
        if (rule.kind === "absolute_range") {
          const ruleId = rule.id;
          // Check if this is a silence or override rule
          if (ruleId.startsWith("silence:")) {
            colorMap[ruleId] = textColor;
          } else if (ruleId.startsWith("override:")) {
            colorMap[ruleId] = orangeColor;
          }
        }
      });
    }

    return colorMap;
  }, [exceptions, schedule, theme.colors.warning, theme.colors.text]);

  const legendSchedules = useMemo(
    () => [...(schedule ? [schedule] : []), ...(scheduleHistory ?? []).map((entry) => entry.schedule)],
    [schedule, scheduleHistory],
  );

  const handleGoToToday = useCallback(() => {
    flatListRef.current?.scrollToIndex({ index: todayPeriodIndex, animated: true });
    setCurrentWeekIndex(todayPeriodIndex);
    setCurrentPeriodIndex(todayPeriodIndex);
  }, [todayPeriodIndex]);

  const isCurrentWeek = useMemo(() => {
    return currentWeekIndex === todayPeriodIndex;
  }, [currentWeekIndex, todayPeriodIndex]);

  const renderWeek = useCallback(
    ({ item: weekDates }: { item: Date[] }) => {
      return (
        <View
          style={{
            width,
            flexDirection: "row",
            backgroundColor: theme.colors.surface as string,
          }}
        >
          {weekDates.map((d) => {
            const daySchedule = resolveScheduleForDate(d, schedule, scheduleHistory, timezone, isHistoryLoading);
            return (
              <DayColumn
                key={formatISO(d, { representation: "date" })}
                date={d}
                exceptionColors={exceptionColors}
                isToday={format(d, "yyyy-MM-dd") === format(today, "yyyy-MM-dd")}
                schedule={daySchedule}
                exceptions={exceptions}
                timezone={timezone}
              />
            );
          })}
        </View>
      );
    },
    [
      exceptionColors,
      schedule,
      scheduleHistory,
      isHistoryLoading,
      exceptions,
      theme.colors.surface,
      width,
      timezone,
      today,
    ],
  );

  return (
    <View flex={1} bg="surface">
      <WeekNavigationBar
        label={currentWeekLabel}
        canGoPrevious={canNavigate.prev}
        canGoNext={canNavigate.next}
        onPrevious={() => scrollToPeriod(-1)}
        onNext={() => scrollToPeriod(1)}
        isCurrentWeek={isCurrentWeek}
        onGoToToday={handleGoToToday}
      />
      <View
        style={{ width: "100%" }}
        onLayout={(event) => {
          const nextWidth = event.nativeEvent.layout.width;
          if (nextWidth > 0 && Math.round(nextWidth) !== Math.round(containerWidth)) {
            setContainerWidth(nextWidth);
          }
        }}
      >
        {isLoading ?
          <View style={{ height: 280, justifyContent: "center", alignItems: "center" }}>
            <ActivityIndicator />
          </View>
        : <FlatList
            ref={flatListRef}
            data={periods}
            keyExtractor={(_, i) => String(i)}
            renderItem={renderWeek}
            horizontal
            showsHorizontalScrollIndicator={false}
            snapToInterval={width}
            decelerationRate="fast"
            bounces={false}
            getItemLayout={(_, index) => ({
              length: width,
              offset: width * index,
              index,
            })}
            onMomentumScrollEnd={(event) => {
              const index = Math.round(event.nativeEvent.contentOffset.x / width);
              setCurrentWeekIndex(index);
              setCurrentPeriodIndex(index);
            }}
            removeClippedSubviews={false}
            maxToRenderPerBatch={5}
            windowSize={5}
            initialNumToRender={periods.length}
          />
        }
      </View>
      <View>
        <Legend exceptions={exceptions} schedules={legendSchedules} />
      </View>
    </View>
  );
});

export const WeeklySchedule = memo(WeeklyScheduleComponent);
